import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Drawer } from 'rsuite';

import HomeIcon from '@rsuite/icons/legacy/Home';
import FileTextIcon from '@rsuite/icons/legacy/FileText';
import ListIcon from '@rsuite/icons/List';
import PlusIcon from '@rsuite/icons/Plus';
import PeoplesIcon from '@rsuite/icons/Peoples';
import CalculatorIcon from '@rsuite/icons/legacy/Calculator';
import WarningIcon from '@rsuite/icons/legacy/Warning';
import TruckIcon from '@rsuite/icons/legacy/Truck';
import SearchIcon from '@rsuite/icons/Search';
import WechatIcon from '@rsuite/icons/Wechat';
import StarIcon from '@rsuite/icons/Star';
import InfoIcon from '@rsuite/icons/legacy/Info';
import NoticeIcon from '@rsuite/icons/Notice';
import MemberIcon from '@rsuite/icons/Member';
import { navSections } from '../../config/navSections';
import InstallAppButton from '../InstallAppButton';

import './navMenu.css';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';
import { getIsInvitedUserFromStorage } from '../../hooks/useAuthRedirect';
import { useMessenger } from '../../context/messengerContext';
import { getProfile } from '../../api/user';

const NAV_ICONS = {
    Home: HomeIcon,
    FileText: FileTextIcon,
    List: ListIcon,
    Plus: PlusIcon,
    Peoples: PeoplesIcon,
    Calculator: CalculatorIcon,
    Warning: WarningIcon,
    Truck: TruckIcon,
    Search: SearchIcon,
    Wechat: WechatIcon,
    Star: StarIcon,
    Info: InfoIcon,
    NoticeIcon: NoticeIcon,
    Member: MemberIcon,
};

// const HIDE_MENU_PATHS = ['/', '/menu', '/auth', '/auth_contact'];
const HIDE_MENU_PATHS = [];

function BurgerIcon() {
    return (
        <svg
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
        >
            <path
                d='M3 6h18M3 12h18M3 18h18'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
            />
        </svg>
    );
}

export default function NavMenu() {
    const { t } = useTranslation('basic');
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const { platform, userId } = useMessenger();
    const [isSomeAdmin, setIsSomeAdmin] = useState(false);

    const pathname = location.pathname || '';
    const shouldHide = HIDE_MENU_PATHS.some(
        (p) => pathname === p || pathname.startsWith(p + '/'),
    );

    const handleNav = (path) => {
        navigate(path);
        setOpen(false);
    };

    const isIOS26Version = isIOSVersionHigherThan26Version();

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const profile = await getProfile({ platform, userId });
                if (cancelled) return;
                const isAdminOnAnyContract = (Array.isArray(profile?.employees)
                    ? profile.employees
                    : []
                ).some(
                    (e) =>
                        e?.is_admin === true &&
                        Boolean(e?.client?.contract_number),
                );
                setIsSomeAdmin(isAdminOnAnyContract);
            } catch {
                if (cancelled) return;
                setIsSomeAdmin(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [platform, userId]);

    if (shouldHide) return null;

    return (
        <>
            <button
                type='button'
                className='navMenu_burger'
                onClick={() => setOpen((prev) => !prev)}
                aria-label={open ? t('nav_menu_close') : t('nav_menu_open')}
            >
                <BurgerIcon />
            </button>
            <Drawer
                open={open}
                onClose={() => setOpen(false)}
                placement='right'
                size='xs'
                className='navMenu_drawer'
            >
                <Drawer.Header
                    style={{ ...(isIOS26Version && { marginTop: '10vh' }) }}
                >
                    <Drawer.Title>{t('nav_menu_title')}</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body className='navMenu_body'>
                    <div className='navMenu_install'>
                        <InstallAppButton />
                    </div>
                    <nav className='navMenu_list' role='navigation'>
                        <button
                            type='button'
                            className={`navMenu_item ${pathname === '/menu' ? 'navMenu_itemActive' : ''}`}
                            onClick={() => handleNav('/menu')}
                        >
                            <span className='navMenu_itemIcon' aria-hidden>
                                <HomeIcon />
                            </span>
                            <span className='navMenu_itemText paragraph-16M'>
                                {t('nav_home')}
                            </span>
                        </button>
                        {navSections.map(({ path, titleKey, icon }) => {
                            const isActive =
                                pathname === path ||
                                (path !== '/' && pathname.startsWith(path));
                            const IconComponent =
                                NAV_ICONS[icon] || FileTextIcon;

                            // const isInvitedUser = getIsInvitedUserFromStorage();

                            // if (isInvitedUser && path === '/employee_list') return null;
                            if (path === '/employee_list' && !isSomeAdmin)
                                return null;

                            return (
                                <button
                                    key={path}
                                    type='button'
                                    className={`navMenu_item ${isActive ? 'navMenu_itemActive' : ''}`}
                                    onClick={() => handleNav(path)}
                                >
                                    <span
                                        className='navMenu_itemIcon'
                                        aria-hidden
                                    >
                                        <IconComponent />
                                    </span>
                                    <span className='navMenu_itemText paragraph-16M'>
                                        {t(titleKey)}
                                    </span>
                                </button>
                            );
                        })}
                    </nav>
                </Drawer.Body>
            </Drawer>
        </>
    );
}
